import { ol_Feature } from "@omnia/exports/openlayers"
import { LayerTypeId, NutrientType } from "../../generated/enums"
import {
    IDatalayerMapInstance,
    IDatalayerZoneMapInstance,
    INumericValuesViewerOptions,
    ZoneTextLocation,
} from "../../layers/types"
import { NumericValuesViewer } from "../../layers/viewers/numeric"
import { formatChemicalCompoundNameAsUnicode } from "../../utils/format"
import { isNumber } from "../../utils/utils"
import { getNutrientColorProvider } from "../colors"
import { RequirementsZone } from "./requirementsZone"

export class RequirementsLayerViewerFactory {
    static createForNutrient(id: string | NutrientType, units: string = "kg/ha", balance: boolean = false) {
        let nutrient: NutrientType
        let key: string

        if (isNumber(id)) {
            nutrient = <NutrientType>id
            key = NutrientType[nutrient]
        } else {
            key = <string>id
            nutrient = NutrientType[key]
        }

        return RequirementsLayerViewerFactory.create(key, nutrient, key, units, balance)
    }

    static create(
        name: string,
        nutrientType: NutrientType,
        key: string,
        units: string = "kg/ha",
        balance: boolean = false
    ) {
        var options: INumericValuesViewerOptions = {
            dataProperty: null,
            units: units,
            valueFilter: (value) => value > 0,
        }

        let colorProvider = getNutrientColorProvider(key, true)

        options.colorProvider = colorProvider

        let factory = {
            key: name,
            typeId: LayerTypeId.NutrientRequirements,
            name: formatChemicalCompoundNameAsUnicode(name),
            createViewer: (layer) => new RequirementsLayerViewer(nutrientType, layer, name, balance, options),

            colors: colorProvider.getValuedColors(),
        }

        return factory
    }
}

export class RequirementsLayerViewer extends NumericValuesViewer {
    constructor(
        private _nutrientType: NutrientType,
        layer: IDatalayerMapInstance,
        private viewerName: string,
        private balance?: boolean,
        options?: INumericValuesViewerOptions
    ) {
        super(layer, options)
    }

    public getFeatureFillColor(feature: any): number[] {
        const hasPendingChanges = (feature as ol_Feature)?.get("hasPendingChanges")

        if (hasPendingChanges) {
            return [246, 169, 39] //orange
        }

        return super.getFeatureFillColor(feature)
    }

    public getValueFillColor(value: number, alpha: number = 1) {
        if (value <= 0) {
            return [255, 255, 255, 0.4]
        }

        return super.getValueFillColor(value, alpha)
    }

    getValueText(value: number, location?: ZoneTextLocation): string {
        if (location == ZoneTextLocation.MapTooltip) {
            if (!value) {
                return "No " + this.viewerName + " " + (this.balance ? "balance" : "required")
            }
        }

        if (this.balance && isNumber(value)) {
            value = -value
        }

        let text = super.getValueText(value, location)

        if (location == ZoneTextLocation.MapTooltip) {
            text = this.viewerName + ": " + text
        }

        return text
    }

    getZoneText(zone: IDatalayerZoneMapInstance, location: ZoneTextLocation): string {
        let text = super.getZoneText(zone, location)

        let reqZone = <RequirementsZone>zone

        if (reqZone && reqZone.hasUserDefinedRate && reqZone.hasUserDefinedRate()) {
            text += " *"
        }

        return text
    }

    public getZoneValue(zone: { value: any }) {
        return zone.value
    }
}
